import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { MapPoint } from './MapPoint';
import { RaceContestantPosition } from './RaceContestantPosition';
import type { TrackingDeviceStatus } from './../enums/TrackingDeviceStatus';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class TrackingDeviceWithRaceContestantPosition extends BaseObj {
    public ID: number;
    public __id: number;

    public BatteryLevel: number | null;
    public __batterylevel: number | null;

    public CurrentStatus: TrackingDeviceStatus;
    public __currentstatus: TrackingDeviceStatus;

    public DeviceNumber: string | null;
    public __devicenumber: string | null;

    public DisplayName: string | null;
    public __displayname: string | null;

    public EQID: number | null;
    public __eqid: number | null;

    public EventID: number | null;
    public __eventid: number | null;

    public ExpectedStatus: TrackingDeviceStatus;
    public __expectedstatus: TrackingDeviceStatus;

    public IMEI: string | null;
    public __imei: string | null;

    public LastBatteryUpdate: Date | null;
    public __lastbatteryupdate: Date | null;

    public LastEvent: string | null;
    public __lastevent: string | null;

    public LastKnownPosition: MapPoint | null;
    public __lastknownposition: MapPoint | null;

    public LastSeen: Date | null;
    public __lastseen: Date | null;

    public Phonenumber: string;
    public __phonenumber: string;

    public RaceContestantID: number;
    public __racecontestantid: number;

    public RaceContestantPosition: RaceContestantPosition | null;
    public __racecontestantposition: RaceContestantPosition | null;

    public Type: string | null;
    public __type: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        BatteryLevel?: number | null;
        CurrentStatus?: TrackingDeviceStatus;
        DeviceNumber?: string | null;
        DisplayName?: string | null;
        EntityState?: EntityState;
        EQID?: number | null;
        EventID?: number | null;
        ExpectedStatus?: TrackingDeviceStatus;
        IMEI?: string | null;
        LastBatteryUpdate?: Date | null;
        LastEvent?: string | null;
        LastKnownPosition?: MapPoint | null;
        LastSeen?: Date | null;
        Phonenumber?: string | null;
        RaceContestantID?: number;
        RaceContestantPosition?: RaceContestantPosition | null;
        Type?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<TrackingDeviceWithRaceContestantPosition>(
            unref(this.__entityState),
            (states) => {
                if (isRef(this.__entityState)) {
                    this.__entityState.value = states.EntityStateWithChildrenConsidered;
                } else {
                    console.warn(
                        'TrackingDeviceWithRaceContestantPosition tried to set entitystate, but entitystate is not a ref! This should never happen',
                    );
                }
                this.__selfEntityState = states.SelfEntityState;
            },
        );
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.BatteryLevel = values.BatteryLevel ?? null;
        this.__batterylevel = values.BatteryLevel ?? null;
        Object.defineProperty(this, 'BatteryLevel', {
            get(): number | null {
                return this.__batterylevel;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BatteryLevel', this.__batterylevel, value);
                this.__batterylevel = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CurrentStatus = values.CurrentStatus ?? 'Unknown';
        this.__currentstatus = values.CurrentStatus ?? 'Unknown';
        Object.defineProperty(this, 'CurrentStatus', {
            get(): TrackingDeviceStatus {
                return this.__currentstatus;
            },
            set(value: TrackingDeviceStatus) {
                this.__entityStateHandler.propertyChanged('CurrentStatus', this.__currentstatus, value);
                this.__currentstatus = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DeviceNumber = values.DeviceNumber ?? null;
        this.__devicenumber = values.DeviceNumber ?? null;
        Object.defineProperty(this, 'DeviceNumber', {
            get(): string | null {
                return this.__devicenumber;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DeviceNumber', this.__devicenumber, value);
                this.__devicenumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisplayName = values.DisplayName ?? null;
        this.__displayname = values.DisplayName ?? null;
        Object.defineProperty(this, 'DisplayName', {
            get(): string | null {
                return this.__displayname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DisplayName', this.__displayname, value);
                this.__displayname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EQID = values.EQID ?? null;
        this.__eqid = values.EQID ?? null;
        Object.defineProperty(this, 'EQID', {
            get(): number | null {
                return this.__eqid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EQID', this.__eqid, value);
                this.__eqid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? null;
        this.__eventid = values.EventID ?? null;
        Object.defineProperty(this, 'EventID', {
            get(): number | null {
                return this.__eventid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ExpectedStatus = values.ExpectedStatus ?? 'Unknown';
        this.__expectedstatus = values.ExpectedStatus ?? 'Unknown';
        Object.defineProperty(this, 'ExpectedStatus', {
            get(): TrackingDeviceStatus {
                return this.__expectedstatus;
            },
            set(value: TrackingDeviceStatus) {
                this.__entityStateHandler.propertyChanged('ExpectedStatus', this.__expectedstatus, value);
                this.__expectedstatus = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IMEI = values.IMEI ?? null;
        this.__imei = values.IMEI ?? null;
        Object.defineProperty(this, 'IMEI', {
            get(): string | null {
                return this.__imei;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('IMEI', this.__imei, value);
                this.__imei = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastBatteryUpdate = values.LastBatteryUpdate ?? null;
        this.__lastbatteryupdate = values.LastBatteryUpdate ?? null;
        Object.defineProperty(this, 'LastBatteryUpdate', {
            get(): Date | null {
                return this.__lastbatteryupdate;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('LastBatteryUpdate', this.__lastbatteryupdate, value);
                this.__lastbatteryupdate = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastEvent = values.LastEvent ?? null;
        this.__lastevent = values.LastEvent ?? null;
        Object.defineProperty(this, 'LastEvent', {
            get(): string | null {
                return this.__lastevent;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LastEvent', this.__lastevent, value);
                this.__lastevent = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastKnownPosition = values.LastKnownPosition ?? null;
        this.__lastknownposition = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.LastKnownPosition ?? null,
            childField: 'LastKnownPosition',
        });
        Object.defineProperty(this, 'LastKnownPosition', {
            get(): MapPoint | null {
                return this.__lastknownposition;
            },
            set(value: MapPoint | null) {
                this.__lastknownposition = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'LastKnownPosition',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.LastSeen = values.LastSeen ?? null;
        this.__lastseen = values.LastSeen ?? null;
        Object.defineProperty(this, 'LastSeen', {
            get(): Date | null {
                return this.__lastseen;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('LastSeen', this.__lastseen, value);
                this.__lastseen = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Phonenumber = values.Phonenumber ?? '';
        this.__phonenumber = values.Phonenumber ?? '';
        Object.defineProperty(this, 'Phonenumber', {
            get(): string {
                return this.__phonenumber;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Phonenumber', this.__phonenumber, value);
                this.__phonenumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantID = values.RaceContestantID ?? 0;
        this.__racecontestantid = values.RaceContestantID ?? 0;
        Object.defineProperty(this, 'RaceContestantID', {
            get(): number {
                return this.__racecontestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceContestantID', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantPosition = values.RaceContestantPosition ?? null;
        this.__racecontestantposition = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.RaceContestantPosition ?? null,
            childField: 'RaceContestantPosition',
        });
        Object.defineProperty(this, 'RaceContestantPosition', {
            get(): RaceContestantPosition | null {
                return this.__racecontestantposition;
            },
            set(value: RaceContestantPosition | null) {
                this.__racecontestantposition = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'RaceContestantPosition',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.Type = values.Type ?? null;
        this.__type = values.Type ?? null;
        Object.defineProperty(this, 'Type', {
            get(): string | null {
                return this.__type;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Type', this.__type, value);
                this.__type = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"BatteryLevel": ${JSON.stringify(this.BatteryLevel)}` +
            ',' +
            `"CurrentStatus": ${this.CurrentStatus == 'Unknown' ? 0 : this.CurrentStatus == 'VeryLowSamplingRate' ? 1 : this.CurrentStatus == 'LowSamplingRate' ? 2 : this.CurrentStatus == 'NormalSamplingRate' ? 3 : this.CurrentStatus == 'HighSamplingRate' ? 4 : this.CurrentStatus == 'RealtimeSamplingRate' ? 5 : 0}` +
            ',' +
            `"DeviceNumber": ${JSON.stringify(this.DeviceNumber)}` +
            ',' +
            `"DisplayName": ${JSON.stringify(this.DisplayName)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EQID": ${JSON.stringify(this.EQID)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"ExpectedStatus": ${this.ExpectedStatus == 'Unknown' ? 0 : this.ExpectedStatus == 'VeryLowSamplingRate' ? 1 : this.ExpectedStatus == 'LowSamplingRate' ? 2 : this.ExpectedStatus == 'NormalSamplingRate' ? 3 : this.ExpectedStatus == 'HighSamplingRate' ? 4 : this.ExpectedStatus == 'RealtimeSamplingRate' ? 5 : 0}` +
            ',' +
            `"IMEI": ${JSON.stringify(this.IMEI)}` +
            ',' +
            `"LastBatteryUpdate": ${this.LastBatteryUpdate ? '"' + formatDateIso(this.LastBatteryUpdate) + '"' : null}` +
            ',' +
            `"LastEvent": ${JSON.stringify(this.LastEvent)}` +
            ',' +
            `"LastKnownPosition": ${!this.LastKnownPosition ? null : this.LastKnownPosition?.serialize()}` +
            ',' +
            `"LastSeen": ${this.LastSeen ? '"' + formatDateIso(this.LastSeen) + '"' : null}` +
            ',' +
            `"Phonenumber": ${JSON.stringify(this.Phonenumber)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"RaceContestantPosition": ${!this.RaceContestantPosition ? null : this.RaceContestantPosition?.serialize()}` +
            ',' +
            `"Type": ${JSON.stringify(this.Type)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of TrackingDeviceWithRaceContestantPosition from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        BatteryLevel?: number | null;
        CurrentStatus?: TrackingDeviceStatus;
        DeviceNumber?: string | null;
        DisplayName?: string | null;
        EQID?: number | null;
        EventID?: number | null;
        ExpectedStatus?: TrackingDeviceStatus;
        IMEI?: string | null;
        LastBatteryUpdate?: Date | null;
        LastEvent?: string | null;
        LastKnownPosition?: MapPoint | null;
        LastSeen?: Date | null;
        Phonenumber?: string | null;
        RaceContestantID?: number;
        RaceContestantPosition?: RaceContestantPosition | null;
        Type?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): TrackingDeviceWithRaceContestantPosition {
        return new TrackingDeviceWithRaceContestantPosition(values);
    }

    public static _FromServerJson(json: any): TrackingDeviceWithRaceContestantPosition {
        return new TrackingDeviceWithRaceContestantPosition({
            ID: json.ID,
            BatteryLevel: json.BatteryLevel ?? null,
            CurrentStatus:
                json.CurrentStatus == 0
                    ? 'Unknown'
                    : json.CurrentStatus == 1
                      ? 'VeryLowSamplingRate'
                      : json.CurrentStatus == 2
                        ? 'LowSamplingRate'
                        : json.CurrentStatus == 3
                          ? 'NormalSamplingRate'
                          : json.CurrentStatus == 4
                            ? 'HighSamplingRate'
                            : json.CurrentStatus == 5
                              ? 'RealtimeSamplingRate'
                              : 'Unknown',
            DeviceNumber: json.DeviceNumber ?? '',
            DisplayName: json.DisplayName ?? '',
            EQID: json.EQID ?? null,
            EventID: json.EventID ?? null,
            ExpectedStatus:
                json.ExpectedStatus == 0
                    ? 'Unknown'
                    : json.ExpectedStatus == 1
                      ? 'VeryLowSamplingRate'
                      : json.ExpectedStatus == 2
                        ? 'LowSamplingRate'
                        : json.ExpectedStatus == 3
                          ? 'NormalSamplingRate'
                          : json.ExpectedStatus == 4
                            ? 'HighSamplingRate'
                            : json.ExpectedStatus == 5
                              ? 'RealtimeSamplingRate'
                              : 'Unknown',
            IMEI: json.IMEI ?? '',
            LastBatteryUpdate: json.LastBatteryUpdate
                ? json.LastBatteryUpdate instanceof Date
                    ? json.LastBatteryUpdate
                    : new Date(json.LastBatteryUpdate)
                : null,
            LastEvent: json.LastEvent ?? '',
            LastKnownPosition: json.LastKnownPosition ? MapPoint._FromServerJson(json.LastKnownPosition) : null,
            LastSeen: json.LastSeen ? (json.LastSeen instanceof Date ? json.LastSeen : new Date(json.LastSeen)) : null,
            Phonenumber: json.Phonenumber ?? '',
            RaceContestantID: json.RaceContestantID,
            RaceContestantPosition: json.RaceContestantPosition
                ? RaceContestantPosition._FromServerJson(json.RaceContestantPosition)
                : null,
            Type: json.Type ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of TrackingDeviceWithRaceContestantPosition from another instance of TrackingDeviceWithRaceContestantPosition.
     * @param other The TrackingDeviceWithRaceContestantPosition you want to clone
     * @returns A cloned TrackingDeviceWithRaceContestantPosition
     */
    public static FromOther(other: TrackingDeviceWithRaceContestantPosition): TrackingDeviceWithRaceContestantPosition {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return TrackingDeviceWithRaceContestantPosition._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TrackingDeviceWithRaceContestantPosition.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'BatteryLevel'
            | 'CurrentStatus'
            | 'DeviceNumber'
            | 'DisplayName'
            | 'EQID'
            | 'EventID'
            | 'ExpectedStatus'
            | 'IMEI'
            | 'LastBatteryUpdate'
            | 'LastEvent'
            | 'LastKnownPosition'
            | 'LastSeen'
            | 'Phonenumber'
            | 'RaceContestantID'
            | 'RaceContestantPosition'
            | 'Type'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'TrackingDeviceWithRaceContestantPosition';
}
